import { apiFetch, apiFetchBlob } from "./api";
import { generateThumbnail } from "./thumbnail";

export interface JobFileDto {
  jobFileId: number;
  jobId: number;
  originalFileName: string;
  category: string | null;
  description: string | null;
  currentVersionId: number | null;
  currentVersionSeq: number | null;
  currentSizeBytes: number | null;
  currentContentType: string | null;
  currentUploadedAtUtc: string | null;
  currentUploadedByUserName: string | null;
  hasThumbnail: boolean;
  rowVersionBase64: string;
}

export interface JobFileVersionDto {
  jobFileVersionId: number;
  jobFileId: number;
  versionSeq: number;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  sha256Hex: string;
  uploadedAtUtc: string;
  uploadedByUserName: string;
}

export interface JobInternalFileLinkDto {
  jobInternalFileLinkId: number;
  jobId: number;
  linkDisplayText: string;
  comment: string | null;
  rowVersionBase64: string;
}

export const jobFileKeys = {
  list: (jobId: number) => ["jobs", jobId, "files"] as const,
  versions: (jobId: number, fileId: number) => ["jobs", jobId, "files", fileId, "versions"] as const,
  internalLinks: (jobId: number) => ["jobs", jobId, "internal-links"] as const,
};

export const jobFiles = {
  list: (jobId: number) => apiFetch<JobFileDto[]>(`/api/jobs/${jobId}/files`),
  upload: async (jobId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    // Best-effort: null (unsupported type or any failure) simply means no thumbnail part.
    const thumb = await generateThumbnail(file);
    if (thumb) form.append("thumbnail", thumb, "thumbnail.webp");
    return apiFetch<{ jobFileId: number; jobFileVersionId: number; versionSeq: number; hasThumbnail: boolean }>(
      `/api/jobs/${jobId}/files`, { method: "POST", body: form });
  },
  uploadVersion: async (jobId: number, fileId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    // Best-effort: null (unsupported type or any failure) simply means no thumbnail part.
    const thumb = await generateThumbnail(file);
    if (thumb) form.append("thumbnail", thumb, "thumbnail.webp");
    return apiFetch<{ jobFileId: number; jobFileVersionId: number; versionSeq: number; hasThumbnail: boolean }>(
      `/api/jobs/${jobId}/files/${fileId}/versions`, { method: "POST", body: form });
  },
  versions: (jobId: number, fileId: number) =>
    apiFetch<JobFileVersionDto[]>(`/api/jobs/${jobId}/files/${fileId}/versions`),
  downloadUrl: (jobId: number, fileId: number, versionId: number) =>
    `/api/jobs/${jobId}/files/${fileId}/versions/${versionId}/download`,
  thumbnailUrl: (jobId: number, fileId: number, versionId: number) =>
    `/api/jobs/${jobId}/files/${fileId}/versions/${versionId}/thumbnail`,
  remove: (jobId: number, fileId: number) =>
    apiFetch<void>(`/api/jobs/${jobId}/files/${fileId}`, { method: "DELETE" }),

  /**
   * Set the category on a job file. Optimistic concurrency via If-Match
   * header (server prefers the header but also accepts rowVersionBase64 in
   * the body as fallback). Returns the refreshed JobFileDto.
   */
  patchCategory: (
    jobId: number,
    fileId: number,
    body: { category: string | null; rowVersionBase64: string },
  ) =>
    apiFetch<JobFileDto>(`/api/jobs/${jobId}/files/${fileId}`, {
      method: "PATCH",
      headers: { "If-Match": `"${body.rowVersionBase64}"` },
      body: JSON.stringify(body),
    }),

  internalLinks: {
    list: (jobId: number) =>
      apiFetch<JobInternalFileLinkDto[]>(`/api/jobs/${jobId}/internal-links`),
    create: (jobId: number, body: { linkDisplayText: string; comment?: string }) =>
      apiFetch<{ jobInternalFileLinkId: number }>(`/api/jobs/${jobId}/internal-links`, { method: "POST", body: JSON.stringify(body) }),
    update: (jobId: number, id: number, body: { linkDisplayText: string; comment?: string; rowVersionBase64: string }) =>
      apiFetch<void>(`/api/jobs/${jobId}/internal-links/${id}`, { method: "PUT", body: JSON.stringify(body) }),
    remove: (jobId: number, id: number, rowVersionBase64: string) =>
      apiFetch<void>(`/api/jobs/${jobId}/internal-links/${id}?rowVersionBase64=${encodeURIComponent(rowVersionBase64)}`, { method: "DELETE" }),
  },
};

/**
 * Authenticated, browser-safe download of a job file version.
 *
 * Background: the backend route GET /api/jobs/{jobId}/files/{fileId}/versions/{versionId}/download
 * requires a JWT Bearer token (RequireAuthorization("job-files:read")). A
 * native `<a href>` browser navigation cannot attach custom headers, which
 * is why the previous Open-button implementation 401'd. This helper uses
 * `apiFetchBlob` to fetch the bytes with the Bearer token attached, then
 * triggers a synthetic anchor click to save the file with its original
 * filename.
 *
 * @returns Resolves when the download has been initiated (the actual disk
 *          write is handled by the browser after click()). Rejects if the
 *          fetch fails — callers should surface the error via toast.
 */
export async function downloadJobFile(
  jobId: number,
  jobFileId: number,
  jobFileVersionId: number,
  filename: string,
): Promise<void> {
  const blob = await apiFetchBlob(
    jobFiles.downloadUrl(jobId, jobFileId, jobFileVersionId),
  );
  const objectUrl = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Revoke after 60s — the browser needs the URL alive long enough to
  // process the click; immediate revocation can race in some browsers.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
